import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToId, scrollToTop } from "@/lib/scroll";

const LINKS = [
  { label: "Home", href: "/", key: "home" },
  { label: "Prices", href: "#pricing", key: "pricing" },
  { label: "Quote", href: "/quote", key: "quote" },
  { label: "Photos", href: "/photos", key: "photos" },
];

export default function Nav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [active, setActive] = useState(pathname === "/photos" ? "photos" : "home");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
    if (pathname === "/photos") {
      setActive("photos");
      return;
    }
    if (pathname === "/quote") {
      setActive("quote");
      return;
    }
    const ids = ["pricing", "quote"];
    const onScroll = () => {
      let cur = "home";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 130) cur = id;
      }
      setActive(cur);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  const go = (e, href) => {
    e.preventDefault();
    if (href === "/") {
      if (pathname !== "/") navigate("/");
      scrollToTop();
      return;
    }
    if (href.startsWith("#")) {
      if (pathname !== "/") {
        navigate(`/${href}`);
      } else {
        scrollToId(href);
      }
    } else {
      navigate(href);
      window.scrollTo(0, 0);
    }
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#0A0C10]/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <a
          href="/"
          data-testid="nav-logo"
          onClick={(e) => go(e, "/")}
          className="flex items-center rounded-md bg-white px-3 py-1.5 shadow-[0_0_20px_rgba(0,82,255,0.25)]"
        >
          <img
            src="/ap_logo_wordmark.png"
            alt="AP Pumping — We Pump Blow and Go"
            className="h-9 sm:h-10"
          />
        </a>

        <nav className="hidden items-center md:flex">
          {LINKS.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              onClick={(e) => go(e, l.href)}
              className={`relative px-3 py-2.5 font-tech text-xs uppercase tracking-widest transition-colors duration-300 ${
                active === l.key ? "text-white" : "text-slate-300 hover:text-[#4D8DFF]"
              }`}
            >
              {active === l.key && (
                <motion.span
                  layoutId="nav-blue-fade"
                  transition={{ type: "spring", stiffness: 350, damping: 32 }}
                  className="absolute inset-0 bg-gradient-to-r from-[#0052FF]/0 via-[#0052FF]/40 to-[#0052FF]/0 shadow-[0_0_24px_rgba(0,82,255,0.35)]"
                />
              )}
              <span className="relative">{l.label}</span>
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
            className="flex h-10 w-10 items-center justify-center border border-white/20 text-white md:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <a
            href="tel:0450454453"
            data-testid="nav-call-button"
            className="group flex items-center gap-2 bg-[#0052FF] px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#2E74FF] hover:shadow-[0_0_24px_rgba(0,82,255,0.55)] sm:px-5"
          >
            <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
            <span className="hidden xs:inline sm:inline">Call 0450 454 453</span>
            <span className="inline xs:hidden sm:hidden">Call</span>
          </a>
        </div>
      </div>

      {menuOpen && (
        <nav
          data-testid="nav-mobile-menu"
          className="border-t border-white/10 bg-[#0A0C10] md:hidden"
        >
          {LINKS.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-mobile-link-${l.key}`}
              onClick={(e) => go(e, l.href)}
              className={`block border-b border-white/5 px-6 py-4 font-display text-2xl font-bold uppercase tracking-wide transition-colors ${
                active === l.key ? "bg-[#0052FF]/15 text-[#7EB0FF]" : "text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>
      )}
    </header>
  );
}